<script setup lang="ts">
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import { FreeMode, Navigation, Thumbs } from 'swiper/modules'
import { Swiper as ISwiper } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { onMounted, onUnmounted, ref, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { ProductMedia } from '../Interfaces/product-media.interface'
import { Product } from '../Interfaces/product.interface'

import 'photoswipe/style.css'
import 'swiper/css'
import 'swiper/css/navigation'

defineProps<{
    product: Product,
    media: Array<ProductMedia>,
}>()

const swiperRef = ref<ComponentPublicInstance<ISwiper> | null>(null)
const fullSwiper = ref<ISwiper>()
const thumbsSwiper = ref<ISwiper>()

const setFullSwiper = (swiper: ISwiper) => {
    fullSwiper.value = swiper
}

const setThumbsSwiper = (swiper: ISwiper) => {
    thumbsSwiper.value = swiper
}

const { t } = useI18n()

let lightbox: any = null

const showMedia = (mediaId: number) => {
    const el: HTMLElement = swiperRef.value?.$el?.querySelector(`.swiper-slide[data-media-id="${mediaId}"]`)

    if (el && el.dataset.index) {
        fullSwiper.value?.slideTo(parseInt(el.dataset.index))
    }
}

onMounted(() => {
    if (!lightbox) {
        lightbox = new PhotoSwipeLightbox({
            gallery: swiperRef.value?.$el,
            children: 'a',
            showHideAnimationType: 'zoom',
            pswpModule: () => import('photoswipe'),
        })

        lightbox.on('change', () => {
            const { pswp } = lightbox

            fullSwiper.value?.slideTo(pswp.currIndex, 0, false)
        })

        lightbox.init()
    }
})

onUnmounted(() => {
    lightbox?.destroy()
})

defineExpose({
    showMedia,
})
</script>

<template>
    <div class="relative overflow-hidden">
        <Swiper
            ref="swiperRef"
            :modules="[Navigation, Thumbs]"
            :space-between="16"
            :navigation="{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }"
            :thumbs="{
                swiper: thumbsSwiper,
                slideThumbActiveClass: '!border-gray-500',
            }"
            class="rounded-md"
            @swiper="setFullSwiper">
            <SwiperSlide v-for="(mediaItem, index) in media" :key="mediaItem.id" class="overflow-hidden rounded-md swiper-slide" :data-index="index" :data-media-id="mediaItem.id">
                <a :href="mediaItem.size_800px.url" data-pswp-width="800" data-pswp-height="800" target="_blank">
                    <img :src="mediaItem.size_800px.url" :alt="product.title" class="object-cover object-center w-full h-full" width="800" height="800">
                    <div class="absolute inset-0 bg-black bg-opacity-[0.02]"></div>
                </a>
            </SwiperSlide>

            <div class="swiper-button-next"></div>
            <div class="swiper-button-prev"></div>
        </Swiper>

        <Swiper
            :modules="[Thumbs, FreeMode]"
            :free-mode="true"
            :grab-cursor="true"
            :space-between="16"
            :slides-per-view="4"
            :watch-slides-progress="true"
            touch-events-target="container"
            :breakpoints="{
                1024: {
                    slidesPerView: 5,
                },
            }"
            class="mt-6"
            @swiper="setThumbsSwiper">
            <SwiperSlide v-for="mediaItem in media" :key="mediaItem.id" class="overflow-hidden border-2 border-transparent rounded-md cursor-pointer swiper-slide group">
                <img :src="mediaItem.size_800px.url" :alt="product.title" class="object-cover object-center w-full h-full cursor-pointer" width="100" height="100">

                <div class="absolute inset-0 bg-black bg-opacity-[0.02] transition-colors group-hover:bg-opacity-[0.04]"></div>
            </SwiperSlide>
        </Swiper>

        <span v-if="product.is_new && !product.is_sale" class="absolute z-10 top-3 left-3 inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">{{ t('New') }}</span>

        <!-- @if ($variants->type === 'variable' && $product->is_sale && $product->in_stock)
        <span class="absolute z-10 top-3 left-3 inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">{{ __('Discount') }} -{{ $product -> sale_percent }}%</span>
        @endif -->
    </div>
</template>

<style scoped>
.swiper-button-next,
.swiper-button-prev {
    --swiper-navigation-size: 20px;
    --swiper-navigation-color: #fff;
    background-color: #1f2937;
    padding: 1.5rem 1rem
}

.swiper-button-next {
    right: 0
}

.swiper-button-prev {
    left: 0
}
</style>
